import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Box, Snackbar, Alert, Dialog, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import api from '../services/api';

/**
 * Pending Sales (web) — porte do bulk editor do POS (miniboss-pos/src/screens/Bulk.js).
 *
 * Dois modos, mesma linguagem visual:
 * - >= sm: trilho horizontal de cards 230x330, igual ao POS no tablet.
 * - < sm (celular na vertical): o mesmo card deitado, largura cheia, empilhado.
 *   Nada muda de cor ou de regra — só o eixo. Alvos de toque crescem (chips 34,
 *   dropdown 40, tip 42, linhas do menu 44) e o tip usa fonte 16 pra o iOS não
 *   dar zoom sozinho ao focar o campo.
 *
 * Diferenças inevitáveis em relação ao POS (não existem no navegador):
 * - sem banner OFFLINE / offlineSyncService: o site não tem cache local de vendas.
 * - payment methods vêm de /app-payment-methods (sem o filtro por hotel do POS).
 */

const last5 = (value) => {
  if (value === null || value === undefined) return '-----';
  return String(value).slice(-5);
};

const USD_FMT = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatCurrency = (value) => USD_FMT.format(Number(value) || 0);

const computeSubtotal = (sale) => {
  if (!sale) return 0;
  const items = Array.isArray(sale.items) ? sale.items : [];
  if (items.length > 0) {
    return items.reduce((sum, it) => {
      const price = Number(it.price) || 0;
      const qty = Number(it.quantity) || 0;
      return sum + price * qty;
    }, 0);
  }
  return Number(sale.subtotal) || 0;
};

const getPaymentEmoji = (methodName) => {
  const name = methodName?.toLowerCase() || '';
  if (name.includes('cash')) return '💵';
  if (name.includes('credit') || name.includes('card')) return '💳';
  if (name.includes('room') || name.includes('charge')) return '🛎️';
  if (name.includes('square')) return '🟦';
  return '💰';
};

// Mesmos tokens do gluestack usados no POS, resolvidos em hex.
const CHIP_VARIANTS = {
  neutral: { bg: '#f1f5f9', border: '#e2e8f0', text: '#1e293b' },
  pink: { bg: '#fdf4ff', border: '#f5d0fe', text: '#701a75' },
  purple: { bg: '#faf5ff', border: '#e9d5ff', text: '#581c87' },
  green: { bg: '#f0fdf4', border: '#bbf7d0', text: '#14532d' },
  amber: { bg: '#fffbeb', border: '#fde68a', text: '#78350f' },
  blue: { bg: '#eff6ff', border: '#bfdbfe', text: '#1e3a8a' },
  red: { bg: '#fef2f2', border: '#fecaca', text: '#7f1d1d' },
};

const CHIP_SIZES = {
  sm: { h: 24, px: 10, font: 12 },
  md: { h: 28, px: 12, font: 14 },
  touch: { h: 34, px: 14, font: 14 }, // celular: dedo, não cursor
};

const Chip = React.memo(function Chip({
  emoji,
  text,
  selected = false,
  variant = 'neutral',
  size = 'md',
  maxWidth,
  noEllipsis = false,
}) {
  const { h, px, font } = CHIP_SIZES[size] || CHIP_SIZES.md;
  const s = CHIP_VARIANTS[variant] || CHIP_VARIANTS.neutral;

  return (
    <div
      style={{
        height: h,
        paddingLeft: px,
        paddingRight: px,
        borderRadius: 9999,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: selected ? '#22c55e' : s.border,
        background: selected ? '#f0fdf4' : s.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        maxWidth,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {!!emoji && <span style={{ fontSize: font, lineHeight: 1 }}>{emoji}</span>}
      <span
        style={{
          fontSize: font,
          color: selected ? '#14532d' : s.text,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: noEllipsis ? 'clip' : 'ellipsis',
        }}
      >
        {text}
      </span>
    </div>
  );
});

const pressableReset = {
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  font: 'inherit',
};

// Faixa que rola na horizontal sem barra de rolagem à mostra: no celular a barra
// não aparece, no desktop apareceria por cima dos chips.
function HScroll({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        overflowX: 'auto',
        minWidth: 0,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// Celular: coluna simples. Trilho: fila horizontal, align stretch pra todos os
// cards terminarem na mesma altura mesmo quando um tem chip a mais.
function ListWrap({ isMobile, children }) {
  if (isMobile) {
    return <div style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 4, paddingBottom: 8 }}>{children}</div>;
  }
  return (
    <HScroll
      sx={{
        alignItems: 'stretch',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '8px',
        paddingBottom: '8px',
      }}
    >
      {children}
    </HScroll>
  );
}

const label = (stacked) => ({
  fontSize: stacked ? 10 : 9,
  color: '#94a3b8',
  fontWeight: 700,
  letterSpacing: 0.6,
  marginBottom: 5,
});

// ✅ Payment selector: single button if 1 method, dropdown if multiple
function PaymentSegments({ methods, value, onChange, disabled = false, stacked = false }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapRef = useRef(null);
  const hasValue = (value || '').trim().length > 0;

  // Fechar clicando fora / Esc. Nada de overlay em position:fixed aqui: o <main>
  // do Dashboard tem backdrop-filter, então fixed vira absolute e o overlay
  // desliza junto com o scroll.
  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setDropdownOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [dropdownOpen]);

  if ((methods || []).length <= 1) {
    const m = methods?.[0];
    if (!m) return null;
    const name = (m?.name || '').trim();
    const selected = (value || '').toLowerCase() === name.toLowerCase();
    return (
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          onChange(name);
        }}
        disabled={disabled}
        style={{ ...pressableReset, opacity: disabled ? 0.85 : 1 }}
      >
        <Chip
          size={stacked ? 'touch' : 'md'}
          selected={selected}
          variant={selected ? 'purple' : 'neutral'}
          emoji={getPaymentEmoji(name)}
          text={name.toUpperCase()}
          noEllipsis
        />
      </button>
    );
  }

  const selectedMethod = (methods || []).find(
    (m) => (m?.name || '').toLowerCase() === (value || '').toLowerCase()
  );

  return (
    <div ref={wrapRef} style={{ position: 'relative', zIndex: dropdownOpen ? 30 : 10 }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => {
          if (disabled) return;
          setDropdownOpen((o) => !o);
        }}
        disabled={disabled}
        aria-expanded={dropdownOpen}
        style={{ ...pressableReset, width: '100%', opacity: disabled ? 0.85 : 1 }}
      >
        <div
          style={{
            height: stacked ? 40 : 32,
            paddingLeft: 12,
            paddingRight: 12,
            borderRadius: 12,
            borderWidth: 2,
            borderStyle: 'solid',
            borderColor: hasValue ? '#3b82f6' : '#cbd5e1',
            background: hasValue ? '#eff6ff' : '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontSize: stacked ? 15 : 14 }}>
            {selectedMethod ? getPaymentEmoji(selectedMethod.name) : '💳'}
          </span>
          <span
            style={{
              fontSize: stacked ? 13 : 12,
              fontWeight: 800,
              color: hasValue ? '#1d4ed8' : '#64748b',
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {selectedMethod ? selectedMethod.name.toUpperCase() : 'SELECT'}
          </span>
          <span style={{ fontSize: 10, color: hasValue ? '#3b82f6' : '#94a3b8', marginLeft: 2 }}>
            {dropdownOpen ? '▲' : '▼'}
          </span>
        </div>
      </button>

      {/* Dropdown panel */}
      {dropdownOpen && (
        <div
          style={{
            position: 'absolute',
            top: stacked ? 44 : 36,
            left: 0,
            right: 0,
            backgroundColor: '#ffffff',
            borderRadius: 14,
            borderWidth: 1.5,
            borderStyle: 'solid',
            borderColor: '#e2e8f0',
            boxShadow: '0 6px 14px rgba(0,0,0,0.13)',
            zIndex: 999,
            overflow: 'hidden',
            minWidth: 160,
          }}
        >
          {(methods || []).map((m, idx) => {
            const name = (m?.name || '').trim();
            const isSelected = (value || '').toLowerCase() === name.toLowerCase();
            const isLast = idx === methods.length - 1;
            return (
              <button
                type="button"
                key={m.id ?? name}
                onClick={() => {
                  onChange(name);
                  setDropdownOpen(false);
                }}
                style={{ ...pressableReset, display: 'block', width: '100%', textAlign: 'left' }}
              >
                <div
                  style={{
                    paddingTop: stacked ? 13 : 10,
                    paddingBottom: stacked ? 13 : 10,
                    paddingLeft: 14,
                    paddingRight: 14,
                    backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                    borderBottomWidth: isLast ? 0 : 1,
                    borderBottomStyle: 'solid',
                    borderBottomColor: '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{getPaymentEmoji(name)}</span>
                  <span
                    style={{
                      fontSize: stacked ? 14 : 13,
                      fontWeight: isSelected ? 800 : 600,
                      color: isSelected ? '#1d4ed8' : '#374151',
                      flex: 1,
                    }}
                  >
                    {name.toUpperCase()}
                  </span>
                  {isSelected && <span style={{ fontSize: 14, color: '#3b82f6' }}>✓</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TipField({ sale, edit, onTipChange, tipDisabled, isCash, stacked }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: tipDisabled ? 0.4 : 1 }}>
      <span style={{ fontSize: stacked ? 16 : 14 }}>{isCash ? '🚫' : '💚'}</span>
      <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <span
          style={{
            position: 'absolute',
            left: stacked ? 12 : 10,
            zIndex: 1,
            fontSize: stacked ? 14 : 13,
            fontWeight: 800,
            color: tipDisabled ? '#94a3b8' : '#15803d',
          }}
        >
          $
        </span>
        <input
          value={String(edit?.tipText ?? (Number(edit?.tip ?? sale?.tip ?? 0) || 0))}
          onChange={(e) => onTipChange(sale, e.target.value)}
          placeholder="0.00"
          inputMode="decimal"
          enterKeyHint="done"
          disabled={tipDisabled}
          aria-label="Tip"
          style={{
            width: '100%',
            paddingTop: 5,
            paddingBottom: 5,
            paddingLeft: stacked ? 26 : 22,
            paddingRight: 10,
            height: stacked ? 42 : 34,
            borderRadius: 10,
            backgroundColor: tipDisabled ? '#f8fafc' : '#f0fdf4',
            borderWidth: 1.5,
            borderStyle: 'solid',
            borderColor: tipDisabled ? '#e2e8f0' : '#86efac',
            fontWeight: 800,
            // 16px: abaixo disso o iOS dá zoom sozinho quando o campo recebe foco.
            fontSize: stacked ? 16 : 15,
            textAlign: 'center',
            color: tipDisabled ? '#94a3b8' : '#15803d',
            boxSizing: 'border-box',
            outlineOffset: 2,
          }}
        />
      </div>
    </div>
  );
}

function TotalsBox({ sub, tip, total, hasPayment, stacked }) {
  const boxStyle = {
    backgroundColor: hasPayment ? '#eff6ff' : '#f8fafc',
    borderRadius: 12,
    paddingLeft: 10,
    paddingRight: 10,
    paddingTop: 8,
    paddingBottom: 8,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: hasPayment ? '#bfdbfe' : '#e2e8f0',
  };
  const rule = hasPayment ? '#bfdbfe' : '#e2e8f0';

  // Celular: a mesma caixa, deitada — Sub e Tip à esquerda, TOTAL depois da régua.
  if (stacked) {
    // wrap: em tela de 360px com total de 4 dígitos o TOTAL desce em vez de vazar
    return (
      <div style={{ ...boxStyle, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', rowGap: 4 }}>
        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Sub</span>
        <span style={{ fontSize: 11, color: '#475569', fontWeight: 700 }}>{formatCurrency(sub)}</span>
        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Tip</span>
        <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>{formatCurrency(tip)}</span>
        <div style={{ flex: 1 }} />
        <div style={{ width: 1, height: 22, backgroundColor: rule, flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>TOTAL</span>
        <span style={{ fontSize: 16, color: hasPayment ? '#1d4ed8' : '#1e293b', fontWeight: 900 }}>
          {formatCurrency(total)}
        </span>
      </div>
    );
  }

  return (
    <div style={boxStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Sub</span>
        <span style={{ fontSize: 11, color: '#475569', fontWeight: 700 }}>{formatCurrency(sub)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>Tip</span>
        <span style={{ fontSize: 11, color: '#059669', fontWeight: 700 }}>{formatCurrency(tip)}</span>
      </div>
      <div style={{ height: 1, backgroundColor: rule, marginTop: 3, marginBottom: 3 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>TOTAL</span>
        <span style={{ fontSize: 15, color: hasPayment ? '#1d4ed8' : '#1e293b', fontWeight: 900 }}>
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
}

// ✅ BulkSaleRow — card do trilho (230x330) ou card deitado de largura cheia
const BulkSaleRow = React.memo(function BulkSaleRow({
  sale,
  edit,
  paymentMethods,
  onPaymentChange,
  onTipChange,
  emojiMap,
  locked = false,
  stacked = false,
  showSeller = true,
  showHotel = false,
}) {
  const sub = computeSubtotal(sale);
  const tip = Number(edit?.tip ?? sale?.tip ?? 0) || 0;
  const total = sub + tip;

  const saleId = sale?.sale_id ?? sale?.id;
  const localId = sale?.local_sale_id;
  const sellerName = sale?.employee_name || 'Unknown';
  const employeeId = sale?.employee_id;
  const sellerEmoji = emojiMap?.[String(employeeId)] || '👤';

  const payment = (edit?.payment_method ?? sale?.payment_method ?? '').trim();
  const hasPayment = payment.length > 0;

  const isCash = payment.toLowerCase().includes('cash');
  const tipDisabled = locked || isCash;

  const L = label(stacked);

  const frame = {
    background: '#ffffff',
    borderWidth: hasPayment ? 2.5 : 1.5,
    borderStyle: 'solid',
    borderColor: locked ? '#cbd5e1' : hasPayment ? '#3b82f6' : '#e2e8f0',
    borderRadius: 16,
    opacity: locked ? 0.7 : 1,
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  };

  const idBlock = (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <span style={{ ...L, marginBottom: 0 }}>LOCAL ID</span>
        <span style={{ fontSize: stacked ? 17 : 15, fontWeight: 900, color: '#1e293b' }}>
          #{last5(localId)}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <span style={{ ...L, marginBottom: 0 }}>SALE ID</span>
        <Chip size="sm" emoji="🌐" text={`#${last5(saleId)}`} variant="neutral" />
      </div>
    </div>
  );

  // ── CELULAR: mesmo card, deitado ──
  if (stacked) {
    const tags = [];
    if (locked) tags.push(<Chip key="lock" size="sm" emoji="🔒" text="SAVED" variant="amber" />);
    if (showSeller) {
      tags.push(
        <Chip key="seller" size="sm" emoji={sellerEmoji} text={sellerName.toUpperCase()} variant="neutral" maxWidth={170} />
      );
    }
    if (showHotel && sale?.hotel_name) {
      tags.push(
        <Chip key="hotel" size="sm" emoji="🏨" text={String(sale.hotel_name).toUpperCase()} variant="neutral" maxWidth={170} />
      );
    }

    return (
      <div style={{ ...frame, width: '100%', marginBottom: 10, position: 'relative' }}>
        <div style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 10, paddingBottom: 10 }}>
          {idBlock}

          {tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>{tags}</div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div style={L}>PAYMENT</div>
              <PaymentSegments
                methods={paymentMethods}
                value={payment}
                onChange={(name) => onPaymentChange(sale, name)}
                disabled={locked}
                stacked
              />
            </div>
            <div style={{ width: 132, flexShrink: 0 }}>
              <div style={L}>TIP</div>
              <TipField
                sale={sale}
                edit={edit}
                onTipChange={onTipChange}
                tipDisabled={tipDisabled}
                isCash={isCash}
                stacked
              />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <TotalsBox sub={sub} tip={tip} total={total} hasPayment={hasPayment} stacked />
          </div>
        </div>
      </div>
    );
  }

  // ── TRILHO (POS): card 230x330 ──
  // minHeight e não height: com o chip do resort a régua de identidade vira duas
  // linhas e vazaria pra fora da borda. O trilho usa align-items:stretch, então
  // todos os cards continuam com a mesma altura.
  return (
    <div style={{ ...frame, width: 230, minHeight: 330, marginRight: 12, flexShrink: 0 }}>
      {/* ── HEADER: Local ID + Sale ID ── */}
      <div
        style={{
          paddingLeft: 12,
          paddingRight: 12,
          paddingTop: 12,
          paddingBottom: 8,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
          borderBottomColor: '#f1f5f9',
        }}
      >
        {idBlock}
        {locked && (
          <div style={{ marginTop: 4, display: 'flex' }}>
            <Chip size="sm" emoji="🔒" text="SAVED" variant="amber" />
          </div>
        )}
      </div>

      {/* ── PAYMENT ── */}
      <div style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 10, paddingBottom: 4 }}>
        <div style={L}>PAYMENT</div>
        <PaymentSegments
          methods={paymentMethods}
          value={payment}
          onChange={(name) => onPaymentChange(sale, name)}
          disabled={locked}
        />
      </div>

      {/* ── TIP ── */}
      <div style={{ paddingLeft: 12, paddingRight: 12, paddingTop: 6, paddingBottom: 8 }}>
        <div style={L}>TIP</div>
        <TipField
          sale={sale}
          edit={edit}
          onTipChange={onTipChange}
          tipDisabled={tipDisabled}
          isCash={isCash}
          stacked={false}
        />
      </div>

      {/* ── DIVIDER ── */}
      <div style={{ height: 1, backgroundColor: '#f1f5f9', marginLeft: 12, marginRight: 12 }} />

      {/* ── FOOTER: Totais + chips de identidade ── */}
      <div
        style={{
          paddingLeft: 12,
          paddingRight: 12,
          paddingTop: 8,
          paddingBottom: 12,
          marginTop: 'auto',
        }}
      >
        <TotalsBox sub={sub} tip={tip} total={total} hasPayment={hasPayment} stacked={false} />
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 4,
            marginTop: 8,
          }}
        >
          {showSeller && (
            <Chip size="sm" emoji={sellerEmoji} text={sellerName.toUpperCase()} variant="neutral" maxWidth={190} />
          )}
          {showHotel && sale?.hotel_name && (
            <Chip size="sm" emoji="🏨" text={String(sale.hotel_name).toUpperCase()} variant="neutral" maxWidth={190} />
          )}
        </div>
      </div>
    </div>
  );
});

export default function PendingSalesPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [pendingSales, setPendingSales] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [emojiMap, setEmojiMap] = useState({}); // { "30": "😎", ... }

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [sellerFilter, setSellerFilter] = useState('ALL'); // 'ALL' | employee_id
  const [edits, setEdits] = useState({}); // edits[saleId] = { payment_method, tip, tipText }
  const [lastAppliedPayment, setLastAppliedPayment] = useState({});

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

  const isLockedSale = useCallback((s) => {
    const pm = (s?.payment_method || '').trim();
    return pm.length > 0; // ✅ se já tem no servidor, trava
  }, []);

  const initEditsFromSales = useCallback((sales) => {
    const next = {};
    (sales || []).forEach((s) => {
      const saleId = s?.sale_id ?? s?.id;
      if (!saleId) return;
      const existingTip = Number(s?.tip) || 0;
      next[saleId] = {
        payment_method: (s?.payment_method || '').trim(),
        tip: existingTip,
        tipText: String(existingTip).replace(/\.00$/, ''),
      };
    });
    setEdits(next);
  }, []);

  const loadEmojisForSales = useCallback(async (salesList) => {
    try {
      const ids = Array.from(
        new Set(
          (Array.isArray(salesList) ? salesList : [])
            .map((s) => s?.employee_id)
            .filter((x) => x !== null && x !== undefined)
            .map((x) => Number(x))
        )
      );

      if (ids.length === 0) {
        setEmojiMap({});
        return;
      }

      const res = await api.post('/employees/emojis', { employee_ids: ids });
      const rows = Array.isArray(res?.data) ? res.data : [];
      const next = {};
      rows.forEach((r) => {
        if (!r) return;
        if (r.employee_id === null || r.employee_id === undefined) return;
        if (!r.emoji) return;
        next[String(r.employee_id)] = r.emoji;
      });
      setEmojiMap(next);
    } catch (e) {
      console.error('❌ /employees/emojis error:', e?.response?.data || e);
      setEmojiMap({});
    }
  }, []);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [salesRes, methodsRes] = await Promise.all([
        api.get('/sales/pending'),
        api.get('/app-payment-methods'),
      ]);

      // ✅ BLINDAGEM: sempre array (evita tela branca se vier algo inesperado)
      const list = Array.isArray(salesRes?.data) ? salesRes.data : [];

      setPendingSales(list);
      initEditsFromSales(list);
      await loadEmojisForSales(list);

      setPaymentMethods(Array.isArray(methodsRes?.data) ? methodsRes.data : []);
    } catch (e) {
      console.error('❌ loadData error:', e?.response?.data || e);
      setPendingSales([]);
      setPaymentMethods([]);
      setEmojiMap({});
      showToast('Could not load pending sales. Tap Refresh to try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [initEditsFromSales, loadEmojisForSales]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sellerList = useMemo(() => {
    const map = new Map();
    (pendingSales || []).forEach((s) => {
      if (!s?.employee_id) return;
      if (!map.has(s.employee_id)) {
        map.set(s.employee_id, { id: s.employee_id, name: s.employee_name || 'Unknown' });
      }
    });
    return Array.from(map.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [pendingSales]);

  const filteredSales = useMemo(() => {
    if (sellerFilter === 'ALL') return pendingSales || [];
    return (pendingSales || []).filter((s) => String(s.employee_id) === String(sellerFilter));
  }, [pendingSales, sellerFilter]);

  const currentSellerLabel = useMemo(() => {
    if (sellerFilter === 'ALL') return 'ALL';
    const found = sellerList.find((s) => String(s.id) === String(sellerFilter));
    return (found?.name || 'SELLER').toUpperCase();
  }, [sellerFilter, sellerList]);

  // Chips de seller/resort só aparecem quando desambiguam algo: a funcionária
  // vendo as próprias vendas num resort só não precisa deles.
  const showSellerChip = sellerFilter === 'ALL' && sellerList.length > 1;
  const showHotelChip = useMemo(
    () => new Set((pendingSales || []).map((s) => s?.hotel_name).filter(Boolean)).size > 1,
    [pendingSales]
  );

  const onPaymentChange = useCallback(
    (sale, paymentName) => {
      if (isLockedSale(sale)) return; // ✅ trava total
      const saleId = sale?.sale_id ?? sale?.id;
      if (!saleId) return;

      const incoming = (paymentName || '').trim();
      const isCash = incoming.toLowerCase().includes('cash');

      setEdits((prev) => {
        const current = prev[saleId] || {};
        const currentPm = String(current.payment_method ?? sale?.payment_method ?? '').trim();
        const same = currentPm && incoming && currentPm.toLowerCase() === incoming.toLowerCase();

        return {
          ...prev,
          [saleId]: {
            ...current,
            payment_method: same ? '' : incoming,
            // If cash: zero out tip; if deselecting cash: keep existing
            ...(!same && isCash ? { tip: 0, tipText: '0' } : {}),
          },
        };
      });
    },
    [isLockedSale]
  );

  const onTipChange = useCallback(
    (sale, text) => {
      if (isLockedSale(sale)) return; // ✅ trava total
      const saleId = sale?.sale_id ?? sale?.id;
      if (!saleId) return;

      let t = String(text ?? '');
      t = t.replace(/[^\d.]/g, '');
      const parts = t.split('.');
      if (parts.length > 2) t = `${parts[0]}.${parts.slice(1).join('')}`;
      const parts2 = t.split('.');
      if (parts2.length === 2 && parts2[1].length > 2) t = `${parts2[0]}.${parts2[1].slice(0, 2)}`;

      const tipNum = parseFloat(t || '0') || 0;

      setEdits((prev) => ({
        ...prev,
        [saleId]: { ...(prev[saleId] || {}), tipText: t, tip: tipNum },
      }));
    },
    [isLockedSale]
  );

  const onSellerChange = useCallback((nextVal) => {
    setSellerFilter((prev) => (String(prev) === String(nextVal) ? 'ALL' : nextVal));
  }, []);

  // ✅ somente as que realmente podem ser trabalhadas nessa tela (sem pm no servidor)
  const actionableSales = useMemo(
    () => (filteredSales || []).filter((s) => !isLockedSale(s)),
    [filteredSales, isLockedSale]
  );

  const readySales = useMemo(
    () =>
      (actionableSales || []).filter((s) => {
        const saleId = s?.sale_id ?? s?.id;
        const e = saleId ? edits[saleId] : null;
        const pm = (e?.payment_method ?? s?.payment_method ?? '').trim();
        return pm.length > 0;
      }),
    [actionableSales, edits]
  );

  const readyCount = readySales.length;
  const missingCount = (actionableSales?.length || 0) - readyCount;

  // ✅ Check if a given payment method is applied to ALL non-locked visible sales
  const isPaymentAppliedToAll = useCallback(
    (paymentName) => {
      const nonLocked = (filteredSales || []).filter((s) => !isLockedSale(s));
      if (nonLocked.length === 0) return false;
      const name = (paymentName || '').toLowerCase();
      return nonLocked.every((s) => {
        const saleId = s?.sale_id ?? s?.id;
        const e = saleId ? edits[saleId] : null;
        const pm = (e?.payment_method ?? s?.payment_method ?? '').trim().toLowerCase();
        return pm === name;
      });
    },
    [filteredSales, isLockedSale, edits]
  );

  // ✅ Apply one payment method to ALL visible (non-locked) sales
  const applyPaymentToAll = useCallback(
    (paymentName) => {
      const incoming = (paymentName || '').trim();
      if (!incoming) return;
      const isCash = incoming.toLowerCase().includes('cash');

      // Toggle: if this method is already applied to all, clear it
      const alreadyApplied = isPaymentAppliedToAll(incoming);
      setLastAppliedPayment(alreadyApplied ? {} : { paymentName: incoming });

      setEdits((prev) => {
        const next = { ...prev };
        (filteredSales || []).forEach((s) => {
          if (isLockedSale(s)) return;
          const saleId = s?.sale_id ?? s?.id;
          if (!saleId) return;
          const current = next[saleId] || {};
          if (alreadyApplied) {
            next[saleId] = { ...current, payment_method: '' };
          } else {
            next[saleId] = {
              ...current,
              payment_method: incoming,
              ...(isCash ? { tip: 0, tipText: '0' } : {}),
            };
          }
        });
        return next;
      });
    },
    [filteredSales, isLockedSale, isPaymentAppliedToAll]
  );

  // ✅ Confirmação: usou "apply to all" e sobrou venda não-cash com tip $0
  const needsTipConfirmation = useMemo(() => {
    if (!lastAppliedPayment?.paymentName) return false;
    return readySales.some((s) => {
      const saleId = s?.sale_id ?? s?.id;
      const e = saleId ? edits[saleId] : null;
      const tip = Number(e?.tip ?? s?.tip ?? 0) || 0;
      const isCash = (e?.payment_method ?? '').toLowerCase().includes('cash');
      return !isCash && tip === 0;
    });
  }, [readySales, edits, lastAppliedPayment]);

  const handleSendAll = useCallback(async () => {
    if (readySales.length === 0) {
      showToast('Nothing to send. Select a payment method for at least 1 sale.', 'warning');
      return;
    }

    setIsProcessing(true);
    try {
      for (const s of readySales) {
        const saleId = s?.sale_id ?? s?.id;
        if (!saleId) continue;
        if (isLockedSale(s)) continue; // Segurança: nunca mexe em vendas já salvas

        const e = edits[saleId] || {};
        const payment_method = (e?.payment_method ?? s?.payment_method ?? '').trim();
        const tip = Number(e?.tip ?? s?.tip ?? 0) || 0;

        // eslint-disable-next-line no-await-in-loop
        await api.put(`/app-sales/${saleId}/finalize`, { payment_method, tip });
      }

      showToast(
        `Sent ${readySales.length} sale(s)` + (missingCount > 0 ? ` • Skipped ${missingCount}` : '')
      );
      setLastAppliedPayment({});
      await loadData();
    } catch (e) {
      console.error('❌ finalize error:', e?.response?.data || e);
      showToast('Failed to finalize sales. Try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  }, [readySales, missingCount, edits, loadData, isLockedSale]);

  const hairline = <div style={{ height: 1, background: '#e2e8f0' }} />;
  const vRule = (
    <div
      style={{
        width: 2,
        height: 36,
        background: '#e2e8f0',
        borderRadius: 9999,
        opacity: 0.8,
        marginLeft: 4,
        marginRight: 4,
        flexShrink: 0,
      }}
    />
  );

  // Home e Refresh saíram: o menu de navegação e o ⟳ do topo do site já fazem os
  // dois (o ⟳ remonta a rota, o que refaz o loadData daqui).
  const sellerStrip = (
    <HScroll sx={{ flex: isMobile ? 1 : '0 1 auto' }}>
      <div style={{ display: 'flex', gap: isMobile ? 6 : 4, alignItems: 'center', flexWrap: 'nowrap' }}>
        {(sellerList || []).map((s) => {
          const selected = String(sellerFilter) === String(s.id);
          const empEmoji = emojiMap?.[String(s.id)] || '👤';
          return (
            <button key={s.id} type="button" onClick={() => onSellerChange(s.id)} style={pressableReset}>
              <Chip
                size={isMobile ? 'touch' : 'md'}
                emoji={selected ? '✅' : empEmoji}
                text={String(s.name || 'Unknown').toUpperCase()}
                variant={selected ? 'purple' : 'neutral'}
                selected={selected}
                maxWidth={240}
              />
            </button>
          );
        })}
      </div>
    </HScroll>
  );

  const applyToAllStrip = paymentMethods.length > 0 && (
    <HScroll sx={{ gap: isMobile ? 0.75 : 0.5, alignItems: 'center' }}>
      {paymentMethods.map((m) => {
        const name = (m?.name || '').trim();
        const appliedToAll = isPaymentAppliedToAll(name);
        return (
          <button key={m.id ?? name} type="button" onClick={() => applyPaymentToAll(name)} style={pressableReset}>
            <div
              style={{
                height: isMobile ? 34 : 28,
                paddingLeft: isMobile ? 12 : 8,
                paddingRight: isMobile ? 12 : 8,
                borderRadius: 9999,
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: appliedToAll ? '#22c55e' : '#e2e8f0',
                background: appliedToAll ? '#f0fdf4' : '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                boxSizing: 'border-box',
              }}
            >
              <span style={{ fontSize: 14 }}>{appliedToAll ? '✅' : getPaymentEmoji(name)}</span>
              <span
                style={{
                  fontSize: 14,
                  color: appliedToAll ? '#14532d' : '#000',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                }}
              >
                {name.toUpperCase()}
              </span>
            </div>
          </button>
        );
      })}
    </HScroll>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 90px)',
        background: '#f8fafc',
      }}
    >
      {/* TOP BAR */}
      {isMobile ? (
        // Celular: as mesmas peças, empilhadas. Cada linha aqui é altura que sai dos
        // cards, então só sobra o que a funcionária realmente usa no resort.
        <div
          style={{
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
            // 12 embaixo pra régua não encostar nos chips — mesmo respiro que a
            // lista tem do outro lado da linha.
            paddingBottom: 12,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {sellerList.length > 1 && sellerStrip}

          {paymentMethods.length > 0 && (
            <>
              {sellerList.length > 1 && hairline}
              <div>
                <div style={{ ...label(true), marginBottom: 6 }}>APPLY TO ALL</div>
                {applyToAllStrip}
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={{ paddingLeft: 8, paddingRight: 8, paddingTop: 8, marginBottom: 4 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 0 }}>
            {sellerList.length > 1 && (
              <>
                {sellerStrip}
                {vRule}
              </>
            )}
            {applyToAllStrip}
          </div>
        </div>
      )}

      {hairline}

      {/* LIST */}
      <div style={{ flex: 1, paddingTop: 8, minHeight: 0 }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <span style={{ color: '#64748b' }}>Loading...</span>
          </div>
        ) : (filteredSales || []).length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              padding: 48,
            }}
          >
            <span style={{ color: '#94a3b8' }}>No pending sales</span>
            <span style={{ color: '#cbd5e1', fontSize: 12 }}>Everything here is finalized.</span>
          </div>
        ) : (
          <ListWrap isMobile={isMobile}>
            {filteredSales.map((sale, index) => {
              const saleId = sale?.sale_id ?? sale?.id;
              return (
                <BulkSaleRow
                  key={String(saleId ?? `idx-${index}`)}
                  sale={sale}
                  edit={saleId ? edits[saleId] : null}
                  paymentMethods={paymentMethods}
                  onPaymentChange={onPaymentChange}
                  onTipChange={onTipChange}
                  emojiMap={emojiMap}
                  locked={isLockedSale(sale)}
                  stacked={isMobile}
                  showSeller={isMobile ? showSellerChip : true}
                  showHotel={showHotelChip}
                />
              );
            })}
          </ListWrap>
        )}
      </div>

      {/* CONFIRMATION MODAL — em Dialog (portal) porque position:fixed dentro do
          <main> com backdrop-filter não gruda na viewport. */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth={false}
        PaperProps={{
          style: {
            background: 'linear-gradient(135deg, #ec4899, #a855f7, #6366f1)',
            borderRadius: 28,
            padding: 3,
            width: '100%',
            maxWidth: 380,
            margin: 24,
            overflow: 'hidden',
          },
        }}
      >
        <div
          style={{
            backgroundColor: '#fafafa',
            borderRadius: 26,
            paddingLeft: 22,
            paddingRight: 22,
            paddingTop: 24,
            paddingBottom: 24,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 32 }}>⚠️</span>
          <span style={{ fontSize: 18, fontWeight: 900, color: '#1f2937', textAlign: 'center' }}>
            Confirm Submission
          </span>
          <span style={{ fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: '20px' }}>
            Some sales have <b style={{ fontWeight: 800, color: '#dc2626' }}>tip = $0.00</b>.
            <br />
            Are you sure you want to send them?
          </span>

          <div style={{ display: 'flex', gap: 8, marginTop: 8, width: '100%' }}>
            <button type="button" style={{ ...pressableReset, flex: 1 }} onClick={() => setShowConfirmModal(false)}>
              <div
                style={{
                  background: 'linear-gradient(135deg, #f87171, #dc2626)',
                  borderRadius: 16,
                  paddingTop: 13,
                  paddingBottom: 13,
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 900,
                  fontSize: 15,
                }}
              >
                ✕&nbsp;&nbsp;NO, GO BACK
              </div>
            </button>

            <button
              type="button"
              style={{ ...pressableReset, flex: 1 }}
              onClick={() => {
                setShowConfirmModal(false);
                handleSendAll();
              }}
            >
              <div
                style={{
                  background: 'linear-gradient(135deg, #34d399, #059669)',
                  borderRadius: 16,
                  paddingTop: 13,
                  paddingBottom: 13,
                  textAlign: 'center',
                  color: 'white',
                  fontWeight: 900,
                  fontSize: 15,
                }}
              >
                ✓&nbsp;&nbsp;YES, SEND
              </div>
            </button>
          </div>
        </div>
      </Dialog>

      {/* BOTTOM BAR — sticky, não fixed: quem rola é o <main> do Dashboard e ele tem
          backdrop-filter, o que quebra position:fixed. */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          marginTop: 'auto',
          paddingLeft: 7,
          paddingRight: 7,
          paddingTop: 4,
          paddingBottom: 4,
        }}
      >
        <div
          style={{
            background: 'linear-gradient(to right, #4853ec, #55a6f7, #2e31eb)',
            borderRadius: 33,
            padding: 3,
          }}
        >
          <div
            style={{
              backgroundColor: '#f3f3f3',
              borderRadius: 26,
              paddingLeft: 14,
              paddingRight: 14,
              paddingTop: 8,
              paddingBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {/* Status badge */}
            <div
              style={{
                flex: 1,
                minWidth: 0,
                backgroundColor: readyCount > 0 ? '#f0fdf4' : '#fafafa',
                borderRadius: 14,
                borderWidth: 1.5,
                borderStyle: 'solid',
                borderColor: readyCount > 0 ? '#86efac' : '#e5e7eb',
                paddingLeft: 12,
                paddingRight: 12,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: '#6b7280',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {sellerFilter === 'ALL' ? '👥 All Sellers' : `👤 ${currentSellerLabel}`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: 2, gap: 6 }}>
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: readyCount > 0 ? '#22c55e' : '#d1d5db',
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: readyCount > 0 ? '#15803d' : '#6b7280',
                  }}
                >
                  {readyCount > 0
                    ? `${readyCount} ready${missingCount > 0 ? ` · ${missingCount} missing` : ''}`
                    : 'No payment selected'}
                </span>
              </div>
            </div>

            {/* SEND BUTTON */}
            <button
              type="button"
              onClick={() => {
                if (isProcessing) return;
                if (readyCount <= 0) {
                  showToast('Nothing to send. Select a payment method for at least 1 sale.', 'warning');
                  return;
                }
                if (needsTipConfirmation) {
                  setShowConfirmModal(true);
                  return;
                }
                handleSendAll();
              }}
              disabled={isProcessing}
              style={{ ...pressableReset, opacity: isProcessing ? 0.85 : 1, flexShrink: 0 }}
            >
              <div
                style={{
                  background: isProcessing
                    ? 'linear-gradient(135deg, #94a3b8, #64748b)'
                    : readyCount > 0
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #cbd5e1, #94a3b8)',
                  borderRadius: 18,
                  paddingTop: 12,
                  paddingBottom: 12,
                  paddingLeft: 20,
                  paddingRight: 20,
                  minWidth: 120,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {isProcessing ? (
                  <span style={{ color: 'white', fontWeight: 800, fontSize: 14 }}>Sending...</span>
                ) : (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: 'white', fontWeight: 900, fontSize: 16 }}>🚀 SEND</span>
                      {readyCount > 0 && (
                        <span
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.25)',
                            borderRadius: 10,
                            paddingLeft: 7,
                            paddingRight: 7,
                            paddingTop: 2,
                            paddingBottom: 2,
                            color: 'white',
                            fontWeight: 900,
                            fontSize: 13,
                          }}
                        >
                          {readyCount}
                        </span>
                      )}
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 10, fontWeight: 600 }}>
                      {readyCount > 0
                        ? missingCount > 0
                          ? `skip ${missingCount} ⚠️`
                          : 'all ready ✓'
                        : 'select payment'}
                    </span>
                  </>
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
